import React, { useEffect, useRef } from "react";
import {
  distinctUntilChanged,
  filter,
  catchError,
  tap,
  of,
  map,
  auditTime,
} from "rxjs";
import PropTypes from "prop-types";
import Track from "../../models/Track";

function TrackActions({ track, children, isCollapsed }) {
  const cl = isCollapsed ? "tw-hidden" : "";
  const blockedLabelRef = useRef(null);
  const loadingLabelRef = useRef(null);
  const errorLabelRef = useRef(null);
  const rangeStateRef = useRef(null);

  useEffect(() => {
    const getFailedWarning = (x) => {
      return x ? `\u00A0\u00A0Failed Regions (${x})\u00A0\u00A0` : "";
    };

    const getLoadingWarning = (x) => {
      return x ? `\u00A0\u00A0Loading (${x})\u00A0\u00A0` : "";
    };

    const getProcessWarning = (x) => {
      return x ? "\u00A0\u00A0Processing...\u00A0\u00A0" : "";
    };

    const getRangeWarning = (x) => {
      return x ? "\u00A0\u00A0Zoom in to load more data\u00A0\u00A0" : "";
    };

    function setLabel(elRef, txt) {
      if (elRef.current) {
        elRef.current.innerText = txt;
      }
    }

    function updateLabels(data) {
      const loadingRegions = data?.loadingTracker?.areas?.length || 0;
      const errorRegions = data?.errorTracker?.areas?.length || 0;

      setLabel(loadingLabelRef, getLoadingWarning(loadingRegions));
      setLabel(errorLabelRef, getFailedWarning(errorRegions));
    }

    const sub1 = track.stage.regionState$
      .pipe(
        auditTime(100),
        filter(Boolean),
        map((x) => x[track.id]),
        filter(Boolean),
        tap(updateLabels)
      )
      .subscribe();

    const sub2 = track.processing$
      .pipe(
        distinctUntilChanged(),
        map((x) => getProcessWarning(x)),
        tap((x) => setLabel(blockedLabelRef, x)),
        catchError(() => of(null))
      )
      .subscribe();

    const sub3 = track.isValidRange$
      .pipe(
        distinctUntilChanged(),
        map((x) => getRangeWarning(!x)),
        tap((x) => setLabel(rangeStateRef, x)),
        catchError(() => of(null))
      )
      .subscribe(() => {});

    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
      sub3.unsubscribe();
    };
  }, []);

  return (
    <div className="tw-flex tw-items-center color-2 tw-text-2xs tw-w-full">
      <div className="tw-px-2 tw-text-3xs">
        <i className={`fa ${isCollapsed ? "fa-minus" : "fa-plus"}`} />
      </div>
      {children}
      <div
        className={`tw-text-orange-600 tw-font-bold ${cl}`}
        ref={rangeStateRef}
      />
      <div className={`tw-text-blue-500 ${cl}`} ref={loadingLabelRef} />
      <div className="tw-flex-grow" />{" "}
      <div
        className={`tw-font-bold tw-bg-red-600 tw-rounded tw-text-white ${cl}`}
        ref={blockedLabelRef}
      />
      <div
        className={`tw-text-red-500 tw-font-bold ${cl}`}
        data-tooltip={`
            <div>
              Some regions failed to load. <br/>
              Please, try again later or contact support.
            </div>
          `}
        data-tooltip-dangerous-html="1"
        data-tooltip-class="genome-browser-small-tooltip"
        ref={errorLabelRef}
      />
    </div>
  );
}

TrackActions.propTypes = {
  track: PropTypes.instanceOf(Track).isRequired,
  children: PropTypes.node.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
};

export default React.memo(TrackActions);
